var Type = require("./Type");

/**
 * The <code>CalendarBase</code> class represents a date/datetime/time data type.
 * It handles the formatting and offers several options such as calendar, custom
 * format string, and a subtype.
 *
 * @param {Object} [oConfig] A configuration object that is used to specify the formatting
 * @param {string} [oConfig.format] A custom format string that will be used as template
 * @param {string} [oConfig.calendar] Specifies the calendar that will be used (default: gregorian)
 * @param {string} [oConfig.inputFormat] Specifies the format of the string-formatted date value (uppercase YYYYMMDD)
 * @constructor
 */
function CalendarBase(oConfig) {
	Type.call(this, oConfig);

	this.format = oConfig ? oConfig.format : null;
	this.calendar = oConfig && typeof oConfig.calendar === "string" ? oConfig.calendar.toLowerCase() : null;
	this.inputFormat = oConfig && typeof oConfig.inputFormat == "string" ? oConfig.inputFormat.toUpperCase() : null;
}

CalendarBase.prototype = Object.create(Type.prototype);
CalendarBase.prototype.constructor = CalendarBase;

CalendarBase.LOCAL_TIMEZONE = new Intl.DateTimeFormat().resolvedOptions().timeZone;

CalendarBase.DEFAULT = {
	numberFormat: 14
};

CalendarBase.BUILTIN = {
	14: "mm-dd-yy",
	15: "d-mmm-yy",
	16: "d-mmm",
	17: "mmm-yy",
	18: "h:mm AM/PM",
	19: "h:mm:ss AM/PM",
	20: "h:mm",
	21: "h:mm:ss",
	22: "m/d/yy h:mm",
	45: "mm:ss",
	46: "[h]:mm:ss",
	47: "mmss.0"
};

CalendarBase.oSymbols = {
	"Y": {
		apply: function(sValue, oDate) {
			oDate.setUTCFullYear(sValue);
		}
	},
	"M": {
		apply: function(sValue, oDate) {
			oDate.setUTCMonth(sValue - 1);
		}
	},
	"D": {
		apply: function(sValue, oDate) {
			oDate.setUTCDate(sValue);
		}
	}
};

/**
 * Creates the <code>Style</code> configuration object according to
 * the configuration of this<code>CalendarBase</code> instance.
 *
 * @param {Object} oData Business object that gets bound to the styling template
 * @return {Object} Style descriptor object
 * @protected
 */
CalendarBase.prototype._createConfiguration = function(oData) {
	var oConfig, sKey, sTemplate;

	oConfig = Object.getPrototypeOf(CalendarBase.prototype)._createConfiguration.call(this, oData);

	sTemplate = this.getDefaultFormat(oData);

	/* Evaluate calendar */
	switch (this.calendar) {
		case "islamic":
			sTemplate = "b2" + sTemplate;
			break;
		case "japanese":
			sTemplate = "[$-ja-JP]" + sTemplate;
			break;
		default: // Gregorian calendar is the default case
	}

	oConfig.numberFormat = {
		format: sTemplate
	};

	/* Find possible builtin match */
	for (sKey in CalendarBase.BUILTIN) {
		if (CalendarBase.BUILTIN[sKey] === sTemplate) {
			oConfig.numberFormat = parseInt(sKey, 10);
		}
	}

	return oConfig;
};

/**
 * Returns the default NumberFormat for the specific type
 * and takes a predefined custom format into account.
 *
 * @returns {string|null} NumberFormat template of the type or null
 * @private
 */
CalendarBase.prototype.getDefaultFormat = function() {
	var sTemplate;

	if (typeof this.format === "string" && this.format.match(/^[gdhmsy\s-,.:/]+(AM\/PM)?$/)) {
		sTemplate = this.format;
	} else if (this.calendar === "islamic") {
		sTemplate = "d mmmm yyyy";
	} else if (this.calendar === "japanese") {
		sTemplate = "ge.m.d";
	}

	return sTemplate;
};

/**
 * Returns the value of the given property that is assigned in the line item.
 *
 * @param {string} sProperty Name of the property that contains the value
 * @param {Object} oData Line item that contains data
 * @return {number|null} XLSX-specific date/time format in days since 30.12.1899
 */
CalendarBase.prototype.getValue = function(sProperty, oData) {
	var iTimestamp, vValue;

	vValue = oData[sProperty];

	if (vValue === null || typeof vValue === "undefined" || vValue === "") {
		return null;
	}

	/* Date, SAPUI5 OData Time representation or String/Number wrapper */
	if (typeof vValue === "object") {
		vValue = typeof vValue.ms === 'number' ? vValue.ms : vValue.valueOf();
	}

	/* 1. Unix timestamp as number */
	if (typeof vValue === "number" && !isNaN(vValue)) {
		iTimestamp = vValue;
	}

	/* 2. String based date/time representation */
	if (typeof vValue === "string") {
		iTimestamp = this._parseStringValue(vValue);
	}

	return typeof iTimestamp === "number"
		? this.dateToExcel(iTimestamp, oData) : null;
};

/**
 * Converts a textual date/time representation to a numeric timestamp.
 *
 * @param {string} sValue String representation of the date/time
 * @return {number|null} Numeric timestamp
 * @private
 */
CalendarBase.prototype._parseStringValue = function(sValue) {

	/* 1. Pattern formatted date string */
	if (this.inputFormat && typeof this.inputFormat === "string") {
		return CalendarBase._parseStringDate(sValue, this.inputFormat);
	}

	/* 2. OData date string "/Date(1234567890)/" */
	if (CalendarBase.odataDateRegex.test(sValue)) {
		return CalendarBase._parseOdataDate(sValue);
	}

	/* 3. DateTimeOffset representation 1986-04-13T01:08:00.000+01:00 */
	if (CalendarBase.odataDateTimeOffsetRegex.test(sValue)) {
		return Date.parse(sValue);
	}

	/* 4. Unix timestamp as string */
	if (isFinite(sValue)) {
		return parseInt(sValue, 10);
	}

	return null;
};

/** START Copied and adapted from datajs **/
/**
 * Extracts the milliseconds if the value is a date/time instance or formatted string.
 *
 * @param {any} vValue the value (may be <code>undefined</code> or <code>null</code>)
 * @return {any} the number of milliseconds or the value itself
 */
CalendarBase._parseODataDuration = function(vValue) {
	var parts, days, hours, minutes, seconds, ms;
	// / <summary>Parses a string in xsd:duration format.</summary>
	// / <param name="duration" type="String">Duration value.</param>
	// / <remarks>
	// / This method will return the input, if the input string has a year or a month component.
	// / </remarks>
	// / <returns type="Object">Object representing the time</returns>

	parts = CalendarBase.odataDurationRegex.exec(vValue);

	// Unsupported duration value for Edm.Time OData spec (if year of month is present)
	/* EDITED
	if (parts[2] || parts[3]) {
		return vValue;
	}
	 */

	days = parseInt(parts[4] || 0, 10);
	hours = parseInt(parts[5] || 0, 10);
	minutes = parseInt(parts[6] || 0, 10);
	seconds = parseFloat(parts[7] || 0);

	ms = parts[8];
	if (ms) {
		// Parse ms duration value ignoring any precision/scale as that part is anyway ignored by JS Date conversion!
		ms = parseFloat("0." + ms) * 1000;
	} else {
		ms = 0;
	}

	ms += (seconds * 1000) + (minutes * 60000) + (hours * 3600000) + (days * 86400000);

	if (parts[1] === "-") {
		ms = -ms;
	}

	return ms;
};

CalendarBase._parseTimeOfDay = function(vValue) {
	var parts, hours, minutes, seconds;
	var date = new Date(0);

	parts = CalendarBase.timeOfDayRegex.exec(vValue);
	hours = parseInt(parts[1] || 0, 10);
	minutes = parseInt(parts[2] || 0, 10);
	seconds = parseFloat(parts[4] || 0);
	date.setUTCHours(hours, minutes, seconds);

	return date.getTime();
};

/**
 * Parses an OData date string and returns the corresponding timestamp
 *
 * @param {string} sValue OData date string i.E. "/Date(1524141637807)/"
 * @return {number} Unix timestamp according to OData date including offset
 * @private
 */
CalendarBase._parseOdataDate = function(sValue) {
	var matchArray, date, offset;

	/* The RegExp matches up to 4 groups:
		 * 0: Full match
		 * 1: Timestamp (mandatory)
		 * 2: Timezone offset sign [ + | - ] (optional)
		 * 3: Timezone offset minutes [0-9]{2,3} (optional)
		 */
	matchArray = CalendarBase.odataDateRegex.exec(sValue);
	date = new Date(parseInt(matchArray[1], 10));

	if (matchArray[2] && matchArray[3]) {
		offset = parseInt(matchArray[2] + matchArray[3], 10);
		date.setUTCMinutes(date.getUTCMinutes() - offset);
	}

	return date.getTime();
};

/* The regular expression corresponds to something like this:
 * /Date(123+60)/
 *
 * This first number is date ticks, the + may be a - and is optional,
 * with the second number indicating a timezone offset in minutes.
 *
 * On the wire, the leading and trailing forward slashes are
 * escaped without being required to so the chance of collisions is reduced;
 * however, by the time we see the objects, the characters already
 * look like regular forward slashes.
*/
CalendarBase.odataDateRegex = /^\/Date\((-?\d+)(\+|-)?(\d+)?\)\/$/;

// The captured indices for this expression are:
// 0       - complete input
// 1       - direction
// 2,3,4   - years, months, days
// 5,6,7,8 - hours, minutes, seconds, milliseconds
CalendarBase.odataDurationRegex =
	/^([+-])?P(?:(\d+)Y)?(?:(\d+)M)?(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)(?:\.(\d+))?S)?)?/;

/** END Copied and adapted from datajs */

/* eslint-disable-next-line */
CalendarBase.odataDateTimeOffsetRegex = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[1-2]\d|3[0-1])T([0-1]\d|2[0-3])(:[0-5]\d)(:[0-5]\d(\.\d{1,12})?)?(Z|[+\-](0\d|1[0-2]):[0-5]\d)/;

CalendarBase.dateStringRegex = /([Y]{4,}|[M]{2}|[D]{2})/g;

/* Edm.TimeOfDay can be 00:00-23:59:59.999999999999 which allows to ommit seconds and fractual seconds */
CalendarBase.timeOfDayRegex = /^([0-1]\d|2[0-3]):([0-5]\d)(:([0-5]\d)(.\d{1,12})?)?$/;

/**
 * Converts a JavaScript Date timestamp to an Office Open XML date
 * representation. Office Open XML date epoch starts at 00.01.1900
 * which is 31.12.1899 and is represented as a number of days since
 * beginning of the epoch.
 *
 * The Javascript Date epoch starts at 01.01.1970 and is represented
 * as a number of milliseconds before or after the beginning of the
 * epoch.
 *
 * The amount of days between 01.01.1900 and 01.01.1970 is exactly
 * 25567. According to the wrong offset in Excel (00.01.1900 instead
 * of 01.01.1900) and the known leap year bug in Excel, we need to
 * add two additional days.
 *
 * https://support.microsoft.com/en-us/help/214326/excel-incorrectly-assumes-that-the-year-1900-is-a-leap-year
 *
 * Time specific information is represented as fraction. This means
 * that the value 30.5 represents 30.5 days after the 31.12.1899
 * where.5 stands for half a day. A day is defined as 24 hours, so
 * half a day means 12 hours since the beginning of the day at
 * 00:00:00. If the integer part of the value is greater than 0 date
 * specific information will be present in the cell.
 *
 * This also applies for time formatted cells. Although the cell only
 * displays the time specific part, the editing bar still shows the
 * date specific part. Therefore it is necessary to remove the date
 * specific part from the value in the XLSX file.
 *
 * The input format can contain milliseconds, which cannot be
 * displayed, since no literal is provided for this in the number
 * formatting of the Office Open XML Spreadsheet Format. This causes
 * milliseconds to be rounded to the nearest second, which can result
 * in a difference of one second. Therefore, milliseconds are
 * intentionally cut off during conversion.
 *
 * @param {number} nTimestamp JavaScript Date timestamp
 * @return {number} Amount of days since 31.12.1899
 * @private
 */
CalendarBase.prototype.dateToExcel = function(nTimestamp) {
	var iOffset, fExcelValue;

	/* Increase the offset if date is after February 28th 1900 */
	iOffset = nTimestamp >= -2203891200000 ? 2 : 1;

	/**
	 * Fallback if date is before UTC 31.12.1899 00:00:00
	 * which corresponds to 0 in Excel
	 */
	fExcelValue = Math.max(((nTimestamp - (nTimestamp % 1000)) / (86400 * 1000)) + (25567 + iOffset), 0);

	return fExcelValue;
};

/**
 * The method parses a given string value according to the provided
 * date pattern.
 *
 * Restrictions:
 * Currently only the symbols d, m, y are supported which correspond
 * to day, month, year.
 *
 * @param {string} sValue Date formatted string
 * @param {string} sPattern ISO 8601 pattern that gets applied
 * @return {number|null} UNIX timestamp that represents the parsed date string
 * @private
 */
CalendarBase._parseStringDate = function(sValue, sPattern) {
	var aMatch, oDate, sSubstring;

	oDate = new Date(0);

	do {
		aMatch = CalendarBase.dateStringRegex.exec(sPattern);

		if (aMatch !== null) {
			sSubstring = sValue.slice(aMatch.index, aMatch.index + aMatch[0].length);

			CalendarBase.oSymbols[aMatch[0].charAt(0)].apply(parseInt(sSubstring, 10), oDate);
		}
	} while (aMatch !== null);

	return oDate.getTime();
};

module.exports = CalendarBase;